/**
 * WMN 10× intent field — energy inject → diffuse → RRF rank → learn.
 * Modes: explore (1 settle) → lean (2–3) → committed (4+).
 * refreshSearchInject after late graph load (v2026-08-04b).
 * Exposes window.wmnIntentField (ES5).
 */
(function (window, document) {
  'use strict';

  var FIELD_KEY = 'wmn_intent_field_v1';
  var HALF_LIFE_MS = 1000 * 60 * 45;
  var MAX_NODES = 80;
  var MAX_NEG = 40;
  var DISCOVER_LIMIT = 32;
  var CANDIDATE_CAP = 280;
  var RRF_K = 60;
  /** Iterative spreading-activation hops (genre↔artist↔product↔imprint). */
  var DIFFUSE_HOPS = 3;
  var DIFFUSE_FLOOR = 0.04;
  var MAX_DWELLS = 16;
  var SUBINTENT_MIN_DWELLS = 2;
  /** Learning-that-matters: relative energy gate vs field top / mean / top-N. */
  var HIGH_ENERGY_REL = 0.5;
  var HIGH_ENERGY_MEAN_MULT = 1.15;
  var HIGH_ENERGY_TOP_N = 8;
  var HIGH_ENERGY_FLOOR = 0.08;
  var LEARN_LOW_ENGAGE = 0.1;
  var LEARN_LOW_SKIP = 0.08;
  var LEARN_HIGH_SKIP = 1.35;
  var COOL_SKIP_HIGH = 0.18;
  var COOL_BOUNCE = 0.12;
  /**
   * Intent switch (blues→tango): crush prior mass so thin catalogs can take over.
   * Without this, committed blues energy outlives mode reset and For You sticks.
   */
  var SWITCH_CRUSH = 0.06;
  var SWITCH_KEEP = 0.85;
  var SWITCH_INJECT_BOOST = 2.45;
  /** Title tokens that hint at a blues (or other) pocket. */
  var POCKET_TOKENS = [
    'desert',
    'sahara',
    'mali',
    'tuareg',
    'delta',
    'chicago',
    'bottleneck',
    'mississippi',
    'acoustic',
    'electric',
    'country blues',
    'women',
    'divas',
    'hooker',
    'patton',
    'johnson',
    'leadbelly',
    'king',
    'holiday',
    'bessie'
  ];
  var EDGE_ATTEN = {
    genre_product: 0.62,
    genre_artist: 0.72,
    /** Hierarchy: child stronger than parent-up; siblings weakest. */
    genre_genre_child: 0.4,
    genre_genre_parent: 0.28,
    genre_genre_sibling: 0.22,
    artist_product: 0.68,
    artist_artist: 0.42,
    artist_genre: 0.48,
    product_artist: 0.4,
    product_genre: 0.45,
    product_imprint: 0.3,
    imprint_product: 0.5
  };

  var DEFAULT_WEIGHTS = {
    energy: 1,
    lexical: 1,
    behavioral: 1,
    popular: 0.35,
    listen: 1.25
  };

  function now() {
    return Date.now();
  }

  function safeParse(raw, fallback) {
    try {
      if (!raw) return fallback;
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  function emptyField() {
    return {
      v: 1,
      nodes: {},
      negatives: {},
      query: { stem: '', settles: 0, lastMs: 0, compound: [] },
      channels: {},
      mode: 'explore',
      thermostat: 1,
      cool: 0,
      termWeights: {
        energy: DEFAULT_WEIGHTS.energy,
        lexical: DEFAULT_WEIGHTS.lexical,
        behavioral: DEFAULT_WEIGHTS.behavioral,
        popular: DEFAULT_WEIGHTS.popular,
        listen: DEFAULT_WEIGHTS.listen
      },
      dwells: [],
      subIntent: null,
      t: now()
    };
  }

  function readFieldRaw() {
    try {
      return safeParse(window.sessionStorage.getItem(FIELD_KEY), null);
    } catch (e) {
      return null;
    }
  }

  function writeField(field) {
    try {
      field.t = now();
      window.sessionStorage.setItem(FIELD_KEY, JSON.stringify(field));
    } catch (e) {
      /* ignore */
    }
  }

  function modeFromSettles(n) {
    if (n >= 4) return 'committed';
    if (n >= 2) return 'lean';
    return 'explore';
  }

  function thermostatForMode(mode) {
    if (mode === 'committed') return 0.12;
    if (mode === 'lean') return 0.4;
    return 1;
  }

  function decayMap(map, ageMs) {
    if (!map || !ageMs || ageMs < 1000) return map || {};
    var factor = Math.pow(0.5, ageMs / HALF_LIFE_MS);
    if (factor > 0.98) return map;
    var out = {};
    var k;
    for (k in map) {
      if (!Object.prototype.hasOwnProperty.call(map, k)) continue;
      var v = (map[k] || 0) * factor;
      if (v >= 0.04) out[k] = v;
    }
    return out;
  }

  function capMap(map, max) {
    var keys = Object.keys(map || {});
    if (keys.length <= max) return map || {};
    keys.sort(function (a, b) {
      return (map[b] || 0) - (map[a] || 0);
    });
    var out = {};
    var i;
    for (i = 0; i < max; i++) out[keys[i]] = map[keys[i]];
    return out;
  }

  function applyThermostat(field) {
    var cool = Math.min(0.85, Math.max(0, field.cool || 0));
    field.cool = cool;
    field.thermostat = thermostatForMode(field.mode) * (1 - cool * 0.7);
    return field;
  }

  function getField() {
    var field = readFieldRaw() || emptyField();
    var age = Math.max(0, now() - (field.t || now()));
    field.nodes = decayMap(field.nodes, age);
    field.negatives = decayMap(field.negatives, age);
    if (field.cool && age >= 1000) {
      field.cool = (field.cool || 0) * Math.pow(0.5, age / HALF_LIFE_MS);
      if (field.cool < 0.02) field.cool = 0;
    }
    if (!field.termWeights) {
      field.termWeights = {
        energy: DEFAULT_WEIGHTS.energy,
        lexical: DEFAULT_WEIGHTS.lexical,
        behavioral: DEFAULT_WEIGHTS.behavioral,
        popular: DEFAULT_WEIGHTS.popular,
        listen: DEFAULT_WEIGHTS.listen
      };
    }
    field.mode = modeFromSettles((field.query && field.query.settles) || 0);
    applyThermostat(field);
    return field;
  }

  function normalizeStem(q) {
    return String(q || '')
      .replace(/^\s+|\s+$/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function compoundTokens(stem) {
    var parts = normalizeStem(stem).split(' ');
    var out = [];
    var i;
    for (i = 0; i < parts.length; i++) {
      if (parts[i] && parts[i].length >= 2) out.push(parts[i]);
    }
    return out;
  }

  function graphReady() {
    return !!(
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.getData === 'function' &&
      window.wmnCatalogGraph.getData()
    );
  }

  function graphData() {
    return graphReady() ? window.wmnCatalogGraph.getData() : null;
  }

  /**
   * genres[slug].r → [{ slug, kind }] kind ∈ p|c|s.
   */
  function parseGenreRelated(node) {
    var out = [];
    if (!node || !node.r) return out;
    var i;
    for (i = 0; i < node.r.length; i++) {
      var row = node.r[i];
      if (!row) continue;
      if (typeof row === 'string') {
        out.push({ slug: row, kind: 's' });
      } else if (Object.prototype.toString.call(row) === '[object Array]' && row[0]) {
        out.push({ slug: String(row[0]), kind: String(row[1] || 's') });
      }
    }
    return out;
  }

  function genreRelatedAtten(kind) {
    if (kind === 'c') return EDGE_ATTEN.genre_genre_child;
    if (kind === 'p') return EDGE_ATTEN.genre_genre_parent;
    return EDGE_ATTEN.genre_genre_sibling;
  }

  /** True if two genre slugs share a hierarchy edge. */
  function genresHierRelated(a, b, data) {
    if (!a || !b || a === b || !data || !data.genres) return false;
    var node = data.genres[a];
    if (!node) return false;
    var rels = parseGenreRelated(node);
    var i;
    for (i = 0; i < rels.length; i++) {
      if (rels[i].slug === b) return true;
    }
    return false;
  }

  function inject(id, amount, channel) {
    if (!id || !(amount > 0)) return getField();
    var field = getField();
    field.nodes[id] = (field.nodes[id] || 0) + amount;
    field.nodes = capMap(field.nodes, MAX_NODES);
    if (channel) {
      field.channels[channel] = (field.channels[channel] || 0) + 1;
    }
    field.mode = modeFromSettles((field.query && field.query.settles) || 0);
    applyThermostat(field);
    writeField(field);
    dispatchFieldUpdated(field);
    return field;
  }

  function injectMany(pairs, channel) {
    if (!pairs || !pairs.length) return getField();
    var field = getField();
    var i;
    for (i = 0; i < pairs.length; i++) {
      var row = pairs[i];
      if (!row || !row.id || !(row.amount > 0)) continue;
      field.nodes[row.id] = (field.nodes[row.id] || 0) + row.amount;
      if (channel) field.channels[channel] = (field.channels[channel] || 0) + 1;
    }
    field.nodes = capMap(field.nodes, MAX_NODES);
    field.mode = modeFromSettles((field.query && field.query.settles) || 0);
    applyThermostat(field);
    writeField(field);
    dispatchFieldUpdated(field);
    return field;
  }

  function addNegative(id, amount) {
    if (!id || !(amount > 0)) return getField();
    var field = getField();
    field.negatives[id] = (field.negatives[id] || 0) + amount;
    field.negatives = capMap(field.negatives, MAX_NEG);
    writeField(field);
    dispatchFieldUpdated(field);
    return field;
  }

  function dispatchFieldUpdated(field) {
    try {
      document.dispatchEvent(
        new CustomEvent('wmn:intent-field-updated', { detail: { field: field } })
      );
    } catch (e) {
      /* ignore */
    }
  }

  function topNodes(field, limit, kindPrefix) {
    var pairs = [];
    var nodes = (field && field.nodes) || {};
    var k;
    for (k in nodes) {
      if (!Object.prototype.hasOwnProperty.call(nodes, k)) continue;
      if (kindPrefix && k.indexOf(kindPrefix) !== 0) continue;
      pairs.push({ id: k, w: nodes[k] || 0 });
    }
    pairs.sort(function (a, b) {
      return b.w - a.w;
    });
    return pairs.slice(0, limit || 8);
  }

  /** True when stems share intent (exact, prefix refine, or long shared token). */
  function stemsRelated(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    if (a.indexOf(b) === 0 || b.indexOf(a) === 0) return true;
    var ta = a.split(/[\s-]+/);
    var tb = b.split(/[\s-]+/);
    var i;
    var j;
    for (i = 0; i < ta.length; i++) {
      if (!ta[i] || ta[i].length < 4) continue;
      for (j = 0; j < tb.length; j++) {
        if (ta[i] === tb[j]) return true;
      }
    }
    return false;
  }

  /**
   * Crush field mass that does not belong to the new stem's linked entities.
   * Keeps a thin residue so explore isn't a hard reset; clears stale sub-intent.
   */
  function decayPriorIntentOnSwitch(field, keepIds) {
    keepIds = keepIds || {};
    var nodes = field.nodes || {};
    var next = {};
    var k;
    for (k in nodes) {
      if (!Object.prototype.hasOwnProperty.call(nodes, k)) continue;
      var w = nodes[k] || 0;
      if (!(w > 0)) continue;
      var factor = keepIds[k] ? SWITCH_KEEP : SWITCH_CRUSH;
      var nw = w * factor;
      if (nw >= 0.04) next[k] = nw;
    }
    field.nodes = next;
    field.subIntent = null;
    // Drop dwells that anchored the old pocket — they re-pull primaryIntent.
    field.dwells = [];
    return field;
  }

  function collectKeepIdsForStem(stem) {
    var keep = {};
    if (!stem || !graphReady()) return keep;
    if (typeof window.wmnCatalogGraph.linkQuery !== 'function') return keep;
    var linked = window.wmnCatalogGraph.linkQuery(stem) || [];
    var i;
    for (i = 0; i < linked.length && i < 14; i++) {
      if (linked[i] && linked[i].id) keep[linked[i].id] = true;
    }
    var toks = compoundTokens(stem);
    for (i = 0; i < toks.length; i++) {
      if (toks[i] === stem) continue;
      var sub = window.wmnCatalogGraph.linkQuery(toks[i]) || [];
      if (sub[0] && sub[0].id) keep[sub[0].id] = true;
    }
    // Always keep the obvious genre:/artist: id shaped like the stem slug.
    var slug = String(stem).replace(/\s+/g, '-');
    keep['genre:' + slug] = true;
    keep['genre:' + stem] = true;
    return keep;
  }

  /**
   * Prefer genre/artist nodes aligned with the active query stem so hard-mass
   * lock follows what the user typed after an intent switch, not leftover weight.
   */
  function primaryIntent(field) {
    var stem = (field && field.query && field.query.stem) || '';
    var genres = topNodes(field, 8, 'genre:');
    var artists = topNodes(field, 8, 'artist:');
    var gi;
    if (stem && genres.length) {
      for (gi = 0; gi < genres.length; gi++) {
        var gid = String(genres[gi].id || '');
        var gslug = gid.indexOf('genre:') === 0 ? gid.slice(6) : '';
        if (
          gslug &&
          (stemsRelated(gslug.replace(/-/g, ' '), stem) ||
            gslug.indexOf(stem.replace(/\s+/g, '-')) !== -1 ||
            stem.replace(/\s+/g, '-').indexOf(gslug) !== -1)
        ) {
          return genres[gi];
        }
      }
      // linkQuery top genre for stem beats an unrelated heavier node
      if (graphReady() && typeof window.wmnCatalogGraph.linkQuery === 'function') {
        var linked = window.wmnCatalogGraph.linkQuery(stem) || [];
        var li;
        for (li = 0; li < linked.length; li++) {
          if (linked[li] && String(linked[li].id).indexOf('genre:') === 0) {
            var want = linked[li].id;
            for (gi = 0; gi < genres.length; gi++) {
              if (genres[gi].id === want) return genres[gi];
            }
            // Even if weight is tiny, surface the linked genre as primary intent
            // so committed/lean mass harvests the new family after a switch.
            var w = ((field.nodes || {})[want]) || 0;
            if (w > 0 || linked[li].score >= 40) {
              return { id: want, w: w || linked[li].score / 100 };
            }
          }
        }
      }
    }
    if (genres.length && (!artists.length || genres[0].w >= artists[0].w * 0.85)) {
      return genres[0];
    }
    if (artists.length) {
      if (stem) {
        for (gi = 0; gi < artists.length; gi++) {
          var aslug = String(artists[gi].id || '').replace(/^artist:/, '');
          if (stemsRelated(aslug.replace(/-/g, ' '), stem)) return artists[gi];
        }
      }
      return artists[0];
    }
    var any = topNodes(field, 1, null);
    return any[0] || null;
  }

  /**
   * Nonlinear settle inject: 1→soft, 2–3→strong, 4+→committed blast.
   */
  function settleBoost(settles) {
    if (settles >= 5) return 3.6;
    if (settles >= 4) return 2.8;
    if (settles >= 3) return 2.0;
    if (settles >= 2) return 1.45;
    return 1;
  }

  function algoQueryBlocked(query) {
    try {
      return !!(
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(query)
      );
    } catch (e) {
      return false;
    }
  }

  function onSearchSettle(query, opts) {
    opts = opts || {};
    var stem = normalizeStem(query);
    if (!stem || stem.length < 2) return getField();
    if (algoQueryBlocked(stem)) return getField();

    var field = getField();
    var prevStem = (field.query && field.query.stem) || '';
    var related = !!(prevStem && stemsRelated(prevStem, stem));
    var switched = !!(prevStem && !related);
    var settles = 1;
    if (prevStem === stem || related) {
      settles = ((field.query && field.query.settles) || 0) + 1;
    } else if (
      window.wmnIntentAnalytics &&
      typeof window.wmnIntentAnalytics.getSettleCount === 'function'
    ) {
      settles = Math.max(1, window.wmnIntentAnalytics.getSettleCount(stem) || 1);
    }

    if (switched) {
      decayPriorIntentOnSwitch(field, collectKeepIdsForStem(stem));
    }

    field.query = {
      stem: stem,
      settles: settles,
      lastMs: now(),
      compound: compoundTokens(stem),
      switched: switched ? 1 : 0
    };
    field.mode = modeFromSettles(settles);
    applyThermostat(field);
    writeField(field);

    var boost = settleBoost(settles);
    if (switched) boost *= SWITCH_INJECT_BOOST;
    var pairs = [];
    var linked = [];
    if (graphReady() && typeof window.wmnCatalogGraph.linkQuery === 'function') {
      linked = window.wmnCatalogGraph.linkQuery(stem) || [];
      var li;
      for (li = 0; li < linked.length && li < 10; li++) {
        var row = linked[li];
        if (!row || !row.id) continue;
        var scale = Math.min(1.2, Math.max(0.2, (row.score || 0) / 100));
        var amt = scale * boost * (row.id.indexOf('genre:') === 0 ? 1.35 : 1);
        pairs.push({ id: row.id, amount: amt });
      }
      // Compound tokens (west african blues → west, african, blues)
      var toks = field.query.compound || [];
      var ti;
      for (ti = 0; ti < toks.length; ti++) {
        if (toks[ti] === stem) continue;
        var sub = window.wmnCatalogGraph.linkQuery(toks[ti]) || [];
        if (sub[0] && sub[0].id) {
          pairs.push({
            id: sub[0].id,
            amount: 0.45 * boost * Math.min(1, (sub[0].score || 0) / 100)
          });
        }
      }
    }

    var results = opts.results || [];
    var ri;
    for (ri = 0; ri < results.length && ri < 8; ri++) {
      var item = results[ri];
      if (!item) continue;
      var fall = 1 - ri * 0.08;
      if (item.handle) {
        pairs.push({
          id: 'product:' + item.handle,
          amount: 0.35 * fall * boost
        });
      }
      if (graphReady() && item.handle) {
        var resolved = window.wmnCatalogGraph.resolveProduct(item.handle);
        var gj;
        if (resolved && resolved.genres) {
          for (gj = 0; gj < resolved.genres.length; gj++) {
            pairs.push({
              id: 'genre:' + resolved.genres[gj],
              amount: 0.55 * fall * boost
            });
          }
        }
        if (resolved && resolved.artists) {
          for (gj = 0; gj < resolved.artists.length; gj++) {
            pairs.push({
              id: 'artist:' + resolved.artists[gj],
              amount: 0.4 * fall * boost
            });
          }
        }
      }
    }

    return injectMany(pairs, 'type');
  }

  /**
   * Re-run type inject for the active stem without bumping settle count.
   * Used when the catalog graph arrives after settles already fired (empty
   * linkQuery) so lean/committed can harvest the real genre pocket.
   */
  function refreshSearchInject(opts) {
    opts = opts || {};
    var field = getField();
    var stem = normalizeStem(opts.query || (field.query && field.query.stem) || '');
    if (!stem || stem.length < 2) return field;
    if (algoQueryBlocked(stem)) return field;
    if (!graphReady()) return field;

    var settles = (field.query && field.query.settles) || 1;
    field.query = {
      stem: stem,
      settles: settles,
      lastMs: now(),
      compound: compoundTokens(stem),
      switched: (field.query && field.query.switched) || 0
    };
    field.mode = modeFromSettles(settles);
    applyThermostat(field);
    writeField(field);

    var boost = settleBoost(settles);
    var pairs = [];
    var linked = window.wmnCatalogGraph.linkQuery(stem) || [];
    var li;
    for (li = 0; li < linked.length && li < 10; li++) {
      var row = linked[li];
      if (!row || !row.id) continue;
      var scale = Math.min(1.2, Math.max(0.2, (row.score || 0) / 100));
      var amt = scale * boost * (row.id.indexOf('genre:') === 0 ? 1.35 : 1);
      pairs.push({ id: row.id, amount: amt });
    }
    var toks = field.query.compound || [];
    var ti;
    for (ti = 0; ti < toks.length; ti++) {
      if (toks[ti] === stem) continue;
      var sub = window.wmnCatalogGraph.linkQuery(toks[ti]) || [];
      if (sub[0] && sub[0].id) {
        pairs.push({
          id: sub[0].id,
          amount: 0.45 * boost * Math.min(1, (sub[0].score || 0) / 100)
        });
      }
    }
    var results = opts.results || [];
    var ri;
    for (ri = 0; ri < results.length && ri < 8; ri++) {
      var item = results[ri];
      if (!item) continue;
      var fall = 1 - ri * 0.08;
      if (item.handle) {
        pairs.push({
          id: 'product:' + item.handle,
          amount: 0.35 * fall * boost
        });
      }
      if (item.handle) {
        var resolved = window.wmnCatalogGraph.resolveProduct(item.handle);
        var gj;
        if (resolved && resolved.genres) {
          for (gj = 0; gj < resolved.genres.length; gj++) {
            pairs.push({
              id: 'genre:' + resolved.genres[gj],
              amount: 0.55 * fall * boost
            });
          }
        }
        if (resolved && resolved.artists) {
          for (gj = 0; gj < resolved.artists.length; gj++) {
            pairs.push({
              id: 'artist:' + resolved.artists[gj],
              amount: 0.4 * fall * boost
            });
          }
        }
      }
    }
    if (!pairs.length) return getField();
    return injectMany(pairs, 'type');
  }

  function injectFromProduct(handle, amount, channel) {
    if (!handle || !(amount > 0)) return getField();
    var ch = channel || 'dwell';
    // Explicit listen/ATC always count; hover/visible/click/view are energy-gated.
    if (ch !== 'listen' && ch !== 'atc' && ch !== 'subintent' && ch !== 'type') {
      var scale = learningScale(handle, 'engage', ch);
      if (scale < 0.05) return getField();
      if (scale < 0.95) amount = amount * Math.max(scale, LEARN_LOW_ENGAGE);
    }
    var pairs = [{ id: 'product:' + handle, amount: amount }];
    if (graphReady()) {
      var resolved = window.wmnCatalogGraph.resolveProduct(handle);
      var i;
      if (resolved && resolved.artists) {
        for (i = 0; i < resolved.artists.length; i++) {
          pairs.push({
            id: 'artist:' + resolved.artists[i],
            amount: amount * 0.7
          });
        }
      }
      if (resolved && resolved.genres) {
        for (i = 0; i < resolved.genres.length; i++) {
          pairs.push({
            id: 'genre:' + resolved.genres[i],
            amount: amount * 0.85
          });
        }
      }
    }
    injectMany(pairs, ch);
    if (ch === 'hover' || ch === 'visible' || ch === 'click') {
      noteDwell(handle, amount, ch);
    }
    return getField();
  }

  function onHover(handle, dwellMs) {
    var ms = dwellMs || 500;
    var amt = Math.min(0.9, 0.25 + (ms - 500) / 4000);
    return injectFromProduct(handle, amt, 'hover');
  }

  function onVisible(handle, dwellMs) {
    var amt = Math.min(1.1, 0.45 + ((dwellMs || 1500) - 1500) / 5000);
    return injectFromProduct(handle, amt, 'visible');
  }

  function onClick(handle) {
    return injectFromProduct(handle, 1.8, 'click');
  }

  function primaryGenreKey(field) {
    var primary = primaryIntent(field);
    if (primary && String(primary.id).indexOf('genre:') === 0) {
      return String(primary.id).slice(6);
    }
    var stem = field.query && field.query.stem ? field.query.stem : '';
    if (stem && graphReady()) {
      var data = graphData();
      if (data && data.genres[stem]) return stem;
      // stem "blues" → genre blues
      var linked =
        typeof window.wmnCatalogGraph.linkQuery === 'function'
          ? window.wmnCatalogGraph.linkQuery(stem) || []
          : [];
      var i;
      for (i = 0; i < linked.length; i++) {
        if (linked[i] && String(linked[i].id).indexOf('genre:') === 0) {
          return String(linked[i].id).slice(6);
        }
      }
    }
    return '';
  }

  function noteDwell(handle, weight, channel) {
    if (!handle || !graphReady()) return getField();
    var field = getField();
    var data = graphData();
    var p = data && data.products[handle];
    if (!p) return field;
    var title = p.t || handle;
    var artist = p.a || '';
    var genres = p.g ? p.g.slice() : [];
    // Merge resolveProduct genres (in case product.g is thin on older graphs)
    if (typeof window.wmnCatalogGraph.resolveProduct === 'function') {
      var resolved = window.wmnCatalogGraph.resolveProduct(handle);
      var ri;
      if (resolved && resolved.genres) {
        for (ri = 0; ri < resolved.genres.length; ri++) {
          if (genres.indexOf(resolved.genres[ri]) === -1) {
            genres.push(resolved.genres[ri]);
          }
        }
      }
    }
    // Title-token → genre slug matches (desert → desert-blues-sahara)
    var inferred = genresFromTitleTokens(title, data, primaryGenreKey(field));
    var ii;
    for (ii = 0; ii < inferred.length; ii++) {
      if (genres.indexOf(inferred[ii]) === -1) genres.push(inferred[ii]);
    }
    var dwells = Array.isArray(field.dwells) ? field.dwells.slice() : [];
    // Upsert — strengthen repeat dwells on same handle
    var found = -1;
    var i;
    for (i = 0; i < dwells.length; i++) {
      if (dwells[i] && dwells[i].handle === handle) {
        found = i;
        break;
      }
    }
    var row = {
      handle: handle,
      artist: artist,
      genres: genres,
      title: title,
      w: (found >= 0 ? dwells[found].w || 0 : 0) + (weight || 0.4),
      channel: channel || 'dwell',
      t: now()
    };
    if (found >= 0) dwells.splice(found, 1);
    dwells.push(row);
    while (dwells.length > MAX_DWELLS) dwells.shift();
    field.dwells = dwells;
    writeField(field);
    return inferSubIntent(field);
  }

  function relatedOverlap(aSlug, bSlug, data) {
    if (!aSlug || !bSlug || aSlug === bSlug) return aSlug === bSlug ? 1 : 0;
    var a = data.artists[aSlug];
    var b = data.artists[bSlug];
    if (!a || !b) return 0;
    var score = 0;
    if (a.r && a.r.indexOf(bSlug) !== -1) score += 1;
    if (b.r && b.r.indexOf(aSlug) !== -1) score += 1;
    // shared genres beyond count
    var ag = a.g || [];
    var bg = b.g || [];
    var i;
    for (i = 0; i < ag.length; i++) {
      if (bg.indexOf(ag[i]) !== -1) score += 0.35;
    }
    return score;
  }

  function titleTokenHits(title) {
    var blob = String(title || '').toLowerCase();
    var hits = [];
    var i;
    for (i = 0; i < POCKET_TOKENS.length; i++) {
      if (blob.indexOf(POCKET_TOKENS[i]) !== -1) hits.push(POCKET_TOKENS[i]);
    }
    return hits;
  }

  /** Map title pocket tokens onto catalog genre slugs/names. */
  function genresFromTitleTokens(title, data, primaryG) {
    var out = [];
    if (!data || !data.genres) return out;
    var toks = titleTokenHits(title);
    if (!toks.length) return out;
    var g;
    var t;
    for (g in data.genres) {
      if (!Object.prototype.hasOwnProperty.call(data.genres, g)) continue;
      if (g === primaryG) continue;
      var name = String((data.genres[g] && data.genres[g].n) || '').toLowerCase();
      var slug = String(g).toLowerCase();
      for (t = 0; t < toks.length; t++) {
        if (slug.indexOf(toks[t]) !== -1 || name.indexOf(toks[t]) !== -1) {
          if (out.indexOf(g) === -1) out.push(g);
          break;
        }
      }
    }
    return out;
  }

  /**
   * Prefer subgenres that embed the primary stem (desert-blues-sahara > tuareg
   * when the user searched "blues"), plus catalog hierarchy relatedness.
   */
  function stemRelatedMultiplier(genreSlug, primaryG, stem, data) {
    var g = String(genreSlug || '').toLowerCase();
    var p = String(primaryG || stem || '').toLowerCase();
    if (!g || !p || p.length < 3) return 1;
    var mult = 1;
    if (g === p) return 1;
    if (g.indexOf(p) !== -1) mult += 1.6;
    if (
      g.indexOf(p + '-') === 0 ||
      g.indexOf('-' + p + '-') !== -1 ||
      g.lastIndexOf('-' + p) === g.length - (p.length + 1)
    ) {
      mult += 0.4;
    }
    var stemBits = String(stem || p).toLowerCase().split(/[\s-]+/);
    var bi;
    for (bi = 0; bi < stemBits.length; bi++) {
      if (stemBits[bi].length >= 3 && g.indexOf(stemBits[bi]) !== -1) {
        mult += 0.35;
      }
    }
    // Hierarchy edge to primary (or stem-as-genre) — cleaner than flat co-tags
    var graph = data || graphData();
    if (graph && genresHierRelated(g, p, graph)) {
      mult += 0.85;
    } else if (graph && stem && genresHierRelated(g, String(stem).toLowerCase(), graph)) {
      mult += 0.7;
    }
    return mult;
  }

  function isStemRelatedGenre(genreSlug, primaryG, stem, data) {
    return stemRelatedMultiplier(genreSlug, primaryG, stem, data) > 1.25;
  }

  /**
   * If any stem-related secondary genre is present, it MUST win over unrelated
   * co-tags (tuareg/gnawa/etc.). No soft 75% threshold.
   */
  function topGenrePreferringStem(genreScores, primaryG, stem, data) {
    var bestRelated = null;
    var bestRelatedW = 0;
    var best = null;
    var bestW = 0;
    var k;
    for (k in genreScores) {
      if (!Object.prototype.hasOwnProperty.call(genreScores, k)) continue;
      var w = genreScores[k] || 0;
      if (w > bestW) {
        bestW = w;
        best = k;
      }
      if (isStemRelatedGenre(k, primaryG, stem, data) && w > bestRelatedW) {
        bestRelatedW = w;
        bestRelated = k;
      }
    }
    if (bestRelated) return { key: bestRelated, w: bestRelatedW };
    return best ? { key: best, w: bestW } : null;
  }

  /**
   * Cluster recent dwells into a sub-intent pocket (e.g. desert blues vs delta).
   * Signals: secondary genres, related-artist pockets, title tokens.
   */
  function inferSubIntent(field) {
    field = field || getField();
    var dwells = field.dwells || [];
    if (dwells.length < SUBINTENT_MIN_DWELLS || !graphReady()) {
      return field;
    }
    var data = graphData();
    var primaryG = primaryGenreKey(field);
    var stem = (field.query && field.query.stem) || primaryG || '';
    // Prefer explicit query stem for relatedness even if primaryIntent drifted
    if (field.query && field.query.stem) stem = field.query.stem;
    var genreScores = {};
    var artistScores = {};
    var tokenScores = {};
    var di;
    for (di = 0; di < dwells.length; di++) {
      var d = dwells[di];
      if (!d) continue;
      var w = d.w || 0.4;
      if (d.artist) artistScores[d.artist] = (artistScores[d.artist] || 0) + w;
      var genreList = (d.genres || []).slice();
      // Re-infer from title each time (covers thin product.g on CDN graph)
      var extra = genresFromTitleTokens(d.title, data, primaryG);
      var ei;
      for (ei = 0; ei < extra.length; ei++) {
        if (genreList.indexOf(extra[ei]) === -1) genreList.push(extra[ei]);
      }
      var g;
      for (g = 0; g < genreList.length; g++) {
        var gk = genreList[g];
        if (!gk || gk === primaryG) continue;
        var gMult = stemRelatedMultiplier(gk, primaryG, stem, data);
        var add = w * 1.4 * gMult;
        // Hard dominance for stem-embedded / hierarchy-related subgenres
        if (isStemRelatedGenre(gk, primaryG, stem, data)) add += 8;
        genreScores[gk] = (genreScores[gk] || 0) + add;
      }
      var toks = titleTokenHits(d.title);
      var ti;
      for (ti = 0; ti < toks.length; ti++) {
        tokenScores[toks[ti]] = (tokenScores[toks[ti]] || 0) + w;
      }
    }

    // Related-artist pocket: boost artists that are mutually related among dwells
    var artists = Object.keys(artistScores);
    var ai;
    var aj;
    for (ai = 0; ai < artists.length; ai++) {
      for (aj = ai + 1; aj < artists.length; aj++) {
        var ov = relatedOverlap(artists[ai], artists[aj], data);
        if (ov > 0) {
          var bump =
            ov * 0.55 * Math.min(artistScores[artists[ai]], artistScores[artists[aj]]);
          artistScores[artists[ai]] += bump;
          artistScores[artists[aj]] += bump;
        }
      }
    }

    function topKey(map) {
      var best = null;
      var bestW = 0;
      var k;
      for (k in map) {
        if (!Object.prototype.hasOwnProperty.call(map, k)) continue;
        if (map[k] > bestW) {
          bestW = map[k];
          best = k;
        }
      }
      return best ? { key: best, w: bestW } : null;
    }

    var topGenre = topGenrePreferringStem(genreScores, primaryG, stem, data);
    var topArtist = topKey(artistScores);
    var topToken = topKey(tokenScores);

    // Stem-related genre always wins the genre channel over tokens/artists.
    var winner = null;
    var kind = '';
    if (topGenre && topGenre.w >= 1.1) {
      winner = topGenre;
      kind = 'genre';
    }
    var stemGenreLocked =
      kind === 'genre' && isStemRelatedGenre(winner.key, primaryG, stem, data);
    if (
      !stemGenreLocked &&
      topToken &&
      topToken.w >= 1.0 &&
      (!winner || topToken.w > winner.w * 1.15)
    ) {
      if (!winner || topToken.w > winner.w) {
        winner = topToken;
        kind = 'token';
      }
    }
    if (
      !stemGenreLocked &&
      topArtist &&
      topArtist.w >= 1.2 &&
      artists.length >= 2 &&
      (!winner || (kind !== 'genre' && topArtist.w > winner.w))
    ) {
      winner = topArtist;
      kind = 'artist';
    }

    if (!winner) return field;

    var sub = {
      key: kind + ':' + winner.key,
      kind: kind,
      score: winner.w,
      artists: [],
      genres: [],
      tokens: [],
      handles: [],
      updatedAt: now()
    };

    if (kind === 'genre') {
      sub.genres = [winner.key];
      // Artists from that genre that appear in dwells, else top genre artists
      for (di = 0; di < dwells.length; di++) {
        if (
          dwells[di].genres &&
          dwells[di].genres.indexOf(winner.key) !== -1 &&
          dwells[di].artist
        ) {
          if (sub.artists.indexOf(dwells[di].artist) === -1) {
            sub.artists.push(dwells[di].artist);
          }
          if (sub.handles.indexOf(dwells[di].handle) === -1) {
            sub.handles.push(dwells[di].handle);
          }
        }
      }
      var gnode = data.genres[winner.key];
      if (gnode && gnode.a) {
        var ga;
        for (ga = 0; ga < gnode.a.length && sub.artists.length < 8; ga++) {
          if (sub.artists.indexOf(gnode.a[ga]) === -1) sub.artists.push(gnode.a[ga]);
        }
      }
    } else if (kind === 'token') {
      sub.tokens = [winner.key];
      for (di = 0; di < dwells.length; di++) {
        var hits = titleTokenHits(dwells[di].title);
        if (hits.indexOf(winner.key) === -1) continue;
        if (dwells[di].artist && sub.artists.indexOf(dwells[di].artist) === -1) {
          sub.artists.push(dwells[di].artist);
        }
        if (sub.handles.indexOf(dwells[di].handle) === -1) {
          sub.handles.push(dwells[di].handle);
        }
        var gj;
        for (gj = 0; gj < (dwells[di].genres || []).length; gj++) {
          if (
            dwells[di].genres[gj] !== primaryG &&
            sub.genres.indexOf(dwells[di].genres[gj]) === -1
          ) {
            sub.genres.push(dwells[di].genres[gj]);
          }
        }
      }
    } else if (kind === 'artist') {
      // Pocket = top artist + related dwelled artists
      sub.artists = [winner.key];
      for (ai = 0; ai < artists.length; ai++) {
        if (artists[ai] === winner.key) continue;
        if (relatedOverlap(winner.key, artists[ai], data) > 0) {
          sub.artists.push(artists[ai]);
        }
      }
      for (di = 0; di < dwells.length; di++) {
        if (sub.artists.indexOf(dwells[di].artist) !== -1) {
          if (sub.handles.indexOf(dwells[di].handle) === -1) {
            sub.handles.push(dwells[di].handle);
          }
        }
      }
    }

    var prevKey = field.subIntent && field.subIntent.key;
    field.subIntent = sub;
    writeField(field);

    // Re-seed energy into the pocket when it newly appears or strengthens
    if (sub.key !== prevKey || winner.w >= 1.8) {
      var pairs = [];
      var pi;
      for (pi = 0; pi < sub.genres.length; pi++) {
        pairs.push({ id: 'genre:' + sub.genres[pi], amount: 1.6 });
      }
      for (pi = 0; pi < sub.artists.length && pi < 6; pi++) {
        pairs.push({ id: 'artist:' + sub.artists[pi], amount: 1.25 });
      }
      for (pi = 0; pi < sub.handles.length && pi < 4; pi++) {
        pairs.push({ id: 'product:' + sub.handles[pi], amount: 0.9 });
      }
      if (pairs.length) {
        injectMany(pairs, 'subintent');
        field = getField();
        field.subIntent = sub;
        writeField(field);
      }
      try {
        document.dispatchEvent(
          new CustomEvent('wmn:subintent', { detail: { subIntent: sub } })
        );
      } catch (eSub) {
        /* ignore */
      }
      if (
        window.wmnIntentAnalytics &&
        typeof window.wmnIntentAnalytics.log === 'function'
      ) {
        try {
          window.wmnIntentAnalytics.log({
            type: 'subintent',
            q: (field.query && field.query.stem) || null,
            mode: field.mode,
            meta: {
              key: sub.key,
              kind: sub.kind,
              score: sub.score,
              artists: sub.artists.slice(0, 6),
              genres: sub.genres.slice(0, 4),
              tokens: sub.tokens.slice(0, 4)
            }
          });
        } catch (eLog) {
          /* ignore */
        }
      }
    }
    return getField();
  }

  function matchesSubIntent(item, sub) {
    if (!item || !sub) return false;
    if (sub.handles && item.handle && sub.handles.indexOf(item.handle) !== -1) {
      return true;
    }
    if (sub.artists && item.artistKey && sub.artists.indexOf(item.artistKey) !== -1) {
      return true;
    }
    var i;
    if (sub.genres && item.genreKeys) {
      for (i = 0; i < item.genreKeys.length; i++) {
        if (sub.genres.indexOf(item.genreKeys[i]) !== -1) return true;
      }
    }
    if (sub.tokens && sub.tokens.length) {
      var blob = (
        (item.title || '') +
        ' ' +
        (item.handle || '').replace(/-/g, ' ') +
        ' ' +
        (item.artist || '')
      ).toLowerCase();
      for (i = 0; i < sub.tokens.length; i++) {
        if (blob.indexOf(sub.tokens[i]) !== -1) return true;
      }
    }
    return false;
  }

  function onListen(handle) {
    return injectFromProduct(handle, 2.2, 'listen');
  }

  function onSkip(handle, opts) {
    opts = opts || {};
    if (!handle) return getField();
    var amt = 0.55;
    if (opts.reason === 'scroll_past') {
      amt = opts.dwellMs != null && opts.dwellMs < 120 ? 0.12 : 0.22;
    } else if (opts.reason === 'ignored_result') {
      amt = 0.35;
    }
    // Gate + learn BEFORE negatives — dampening the skipped card must not
    // cancel cool or flip learningScale to the low-energy path.
    var wasHigh = isHighEnergy(handle);
    var scale = learningScale(handle, 'skip', 'skip');
    if (scale < 0.05) return getField();
    try {
      learnFromSignal('skip', false, { handle: handle });
    } catch (e) {
      /* ignore */
    }
    amt = amt * scale;
    addNegative('product:' + handle, amt);
    if (graphReady()) {
      var resolved = window.wmnCatalogGraph.resolveProduct(handle);
      if (resolved && resolved.artists && resolved.artists[0]) {
        addNegative('artist:' + resolved.artists[0], amt * 0.35);
      }
    }
    if (wasHigh) {
      coolThermostat(COOL_SKIP_HIGH);
    }
    return getField();
  }

  /**
   * Bounce clear: results opened then cleared in <800ms with no dwell/click.
   * Do not strengthen intent — cool thermostat when rejecting field-ranked mass.
   */
  function onBounceClear(query, opts) {
    opts = opts || {};
    var field = getField();
    field.channels = field.channels || {};
    field.channels.bounce = (field.channels.bounce || 0) + 1;
    // Keep stem for context but do not bump settles or inject energy.
    var stem = normalizeStem(query);
    if (stem && !(field.query && field.query.stem)) {
      field.query = {
        stem: stem,
        settles: 0,
        lastMs: now(),
        compound: compoundTokens(stem)
      };
    }
    var shouldCool = false;
    var handles = opts.handles || [];
    var hi;
    if (handles.length) {
      for (hi = 0; hi < handles.length; hi++) {
        if (isHighEnergy(handles[hi], field)) {
          shouldCool = true;
          break;
        }
      }
    } else if (field.mode === 'lean' || field.mode === 'committed') {
      // Field-owned ranking was bounced — treat as rejecting recommended mass.
      shouldCool = true;
    }
    applyThermostat(field);
    writeField(field);
    dispatchFieldUpdated(field);
    if (shouldCool) return coolThermostat(COOL_BOUNCE);
    return getField();
  }

  function itemFromHandle(handle) {
    var data = graphData();
    if (!data || !handle || !data.products[handle]) return null;
    var p = data.products[handle];
    var title = p.t || handle;
    var titleL = String(title).toLowerCase();
    if (titleL.indexOf('gift voucher') !== -1 || titleL.indexOf('gift card') !== -1) {
      return null;
    }
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    var artistName = '';
    if (p.a && data.artists[p.a]) artistName = data.artists[p.a].n || p.a;
    var genreLabels = [];
    var gi;
    if (p.g) {
      for (gi = 0; gi < p.g.length; gi++) {
        var gnode = data.genres[p.g[gi]];
        genreLabels.push(gnode && gnode.n ? gnode.n : String(p.g[gi]).replace(/-/g, ' '));
      }
    }
    var img = null;
    if (
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.getProductImage === 'function'
    ) {
      img = window.wmnCatalogGraph.getProductImage(handle) || null;
      if (img === '') img = null;
    }
    return {
      handle: handle,
      title: title,
      artist: artistName,
      url: root + 'products/' + handle,
      image: img,
      genres: genreLabels,
      fromGraph: true,
      artistKey: p.a || '',
      genreKeys: p.g ? p.g.slice() : []
    };
  }

  function softDegree(n) {
    return Math.max(1, Math.sqrt(Math.max(1, n || 1)));
  }

  function negativeDamp(id, field) {
    var neg = (field.negatives && field.negatives[id]) || 0;
    if (!(neg > 0)) return 1;
    return 1 / (1 + neg * 2.2);
  }

  /**
   * Deposit energy onto an entity node for the next hop frontier.
   * Accumulates; later hops continue from the total.
   */
  function deposit(next, id, amount, field) {
    if (!id || !(amount > 0)) return;
    var damped = amount * negativeDamp(id, field);
    if (damped < DIFFUSE_FLOOR * 0.25) return;
    next[id] = (next[id] || 0) + damped;
  }

  /**
   * One spreading-activation hop over the catalog graph.
   * Energy at genres/artists/imprints/products fires to neighbors with
   * edge-type attenuation and soft degree normalization.
   */
  function diffuseHop(energy, field, data) {
    var next = {};
    var id;
    for (id in energy) {
      if (!Object.prototype.hasOwnProperty.call(energy, id)) continue;
      var e = energy[id] || 0;
      if (e < DIFFUSE_FLOOR) continue;
      e *= negativeDamp(id, field);
      if (e < DIFFUSE_FLOOR) continue;

      var parts = String(id).split(':');
      var kind = parts[0];
      var key = parts.slice(1).join(':');
      var i;
      var share;

      if (kind === 'genre') {
        var genre = data.genres[key];
        if (!genre) continue;
        var gp = genre.p || [];
        var ga = genre.a || [];
        share = e * EDGE_ATTEN.genre_product / softDegree(gp.length);
        for (i = 0; i < gp.length; i++) {
          deposit(next, 'product:' + gp[i], share, field);
        }
        share = e * EDGE_ATTEN.genre_artist / softDegree(ga.length);
        for (i = 0; i < ga.length; i++) {
          deposit(next, 'artist:' + ga[i], share, field);
        }
        // Genre hierarchy / relatedness (attenuated — must not drown product mass)
        var grels = parseGenreRelated(genre);
        if (grels.length) {
          share = e / softDegree(grels.length);
          for (i = 0; i < grels.length; i++) {
            var atten = genreRelatedAtten(grels[i].kind);
            deposit(next, 'genre:' + grels[i].slug, share * atten, field);
          }
        }
      } else if (kind === 'artist') {
        var artist = data.artists[key];
        if (!artist) continue;
        var ap = artist.p || [];
        var ar = artist.r || [];
        var ag = artist.g || [];
        share = e * EDGE_ATTEN.artist_product / softDegree(ap.length);
        for (i = 0; i < ap.length; i++) {
          deposit(next, 'product:' + ap[i], share, field);
        }
        share = e * EDGE_ATTEN.artist_artist / softDegree(ar.length);
        for (i = 0; i < ar.length; i++) {
          deposit(next, 'artist:' + ar[i], share, field);
        }
        share = e * EDGE_ATTEN.artist_genre / softDegree(ag.length);
        for (i = 0; i < ag.length; i++) {
          deposit(next, 'genre:' + ag[i], share, field);
        }
      } else if (kind === 'product') {
        var prod = data.products[key];
        if (!prod) continue;
        if (prod.a) {
          deposit(next, 'artist:' + prod.a, e * EDGE_ATTEN.product_artist, field);
        }
        if (prod.g) {
          share = e * EDGE_ATTEN.product_genre / softDegree(prod.g.length);
          for (i = 0; i < prod.g.length; i++) {
            deposit(next, 'genre:' + prod.g[i], share, field);
          }
        }
        if (prod.v) {
          deposit(next, 'imprint:' + prod.v, e * EDGE_ATTEN.product_imprint, field);
        }
        // Retain residual on the product so multi-hop paths still score it
        deposit(next, id, e * 0.22, field);
      } else if (kind === 'imprint') {
        var imprint = data.imprints[key];
        if (!imprint) continue;
        var ip = imprint.p || [];
        share = e * EDGE_ATTEN.imprint_product / softDegree(ip.length);
        for (i = 0; i < ip.length; i++) {
          deposit(next, 'product:' + ip[i], share, field);
        }
      }
    }
    return next;
  }

  /**
   * Iterative path-attenuated spreading activation.
   * Seeds from field.nodes, walks DIFFUSE_HOPS hops, returns product→energy.
   */
  function diffuseProductScores(field) {
    var data = graphData();
    var scores = {};
    if (!data) return scores;

    var energy = {};
    var seeds = topNodes(field, 16, null);
    if (!seeds.length) return scores;
    var si;
    for (si = 0; si < seeds.length; si++) {
      energy[seeds[si].id] = seeds[si].w;
    }

    // Also seed any high negatives as blocked (zero out if present)
    var nk;
    for (nk in field.negatives || {}) {
      if (!Object.prototype.hasOwnProperty.call(field.negatives, nk)) continue;
      if (energy[nk]) energy[nk] *= negativeDamp(nk, field);
    }

    var absorbed = {}; // product handle → cumulative energy across hops
    var hop;
    for (hop = 0; hop < DIFFUSE_HOPS; hop++) {
      var id;
      for (id in energy) {
        if (!Object.prototype.hasOwnProperty.call(energy, id)) continue;
        if (id.indexOf('product:') === 0) {
          var h = id.slice('product:'.length);
          absorbed[h] = (absorbed[h] || 0) + (energy[id] || 0);
        }
      }
      energy = diffuseHop(energy, field, data);
      // Cap frontier size for perf — keep hottest entities
      var frontier = [];
      for (id in energy) {
        if (!Object.prototype.hasOwnProperty.call(energy, id)) continue;
        if ((energy[id] || 0) >= DIFFUSE_FLOOR) {
          frontier.push({ id: id, w: energy[id] });
        }
      }
      frontier.sort(function (a, b) {
        return b.w - a.w;
      });
      if (frontier.length > 220) {
        var trimmed = {};
        var ti;
        for (ti = 0; ti < 220; ti++) {
          trimmed[frontier[ti].id] = frontier[ti].w;
        }
        energy = trimmed;
      }
    }
    // Final hop residual products
    for (id in energy) {
      if (!Object.prototype.hasOwnProperty.call(energy, id)) continue;
      if (id.indexOf('product:') === 0) {
        var h2 = id.slice('product:'.length);
        absorbed[h2] = (absorbed[h2] || 0) + (energy[id] || 0);
      }
    }

    // Apply product-level negatives and drop noise
    var out = {};
    var handle;
    for (handle in absorbed) {
      if (!Object.prototype.hasOwnProperty.call(absorbed, handle)) continue;
      if (!data.products[handle]) continue;
      var score = absorbed[handle] * negativeDamp('product:' + handle, field);
      if (score >= DIFFUSE_FLOOR) out[handle] = score;
    }

    // Cap to candidate budget by energy
    var ranked = Object.keys(out).sort(function (a, b) {
      return out[b] - out[a];
    });
    if (ranked.length > CANDIDATE_CAP) {
      var capped = {};
      var ci;
      for (ci = 0; ci < CANDIDATE_CAP; ci++) {
        capped[ranked[ci]] = out[ranked[ci]];
      }
      return capped;
    }
    return out;
  }

  /** Cheap seed-adjacent energy for harvest misses (no re-diffuse). */
  function productEnergy(handle, field, data) {
    if (!handle || !data || !data.products[handle]) return 0;
    var nodes = field.nodes || {};
    var p = data.products[handle];
    var score = nodes['product:' + handle] || 0;
    if (p.a) score += (nodes['artist:' + p.a] || 0) * 0.55;
    var i;
    if (p.g) {
      for (i = 0; i < p.g.length; i++) {
        score += (nodes['genre:' + p.g[i]] || 0) * 0.7;
      }
    }
    if (p.v) score += (nodes['imprint:' + p.v] || 0) * 0.25;
    return Math.max(0, score * negativeDamp('product:' + handle, field));
  }

  /**
   * Collect product handles that are currently "in play" for energy comparison:
   * seeded products + products of top genre/artist nodes.
   */
  function referenceProductHandles(field, data) {
    var out = {};
    var nodes = (field && field.nodes) || {};
    var id;
    for (id in nodes) {
      if (!Object.prototype.hasOwnProperty.call(nodes, id)) continue;
      if (id.indexOf('product:') === 0) {
        out[id.slice(8)] = true;
      } else if (id.indexOf('genre:') === 0 && data.genres) {
        var g = data.genres[id.slice(6)];
        var gp = g && g.p ? g.p : [];
        var gi;
        for (gi = 0; gi < gp.length && gi < 40; gi++) out[gp[gi]] = true;
      } else if (id.indexOf('artist:') === 0 && data.artists) {
        var a = data.artists[id.slice(7)];
        var ap = a && a.p ? a.p : [];
        var ai;
        for (ai = 0; ai < ap.length && ai < 24; ai++) out[ap[ai]] = true;
      }
    }
    return Object.keys(out);
  }

  /** In-memory diffuse cache — isHighEnergy must match rank energy, not cheap PE. */
  var _diffuseCache = {
    t: 0,
    cool: 0,
    nodeN: -1,
    negN: -1,
    map: null
  };

  function diffuseMapCached(field) {
    field = field || getField();
    var nodeN = Object.keys(field.nodes || {}).length;
    var negN = Object.keys(field.negatives || {}).length;
    var cool = field.cool || 0;
    if (
      _diffuseCache.map &&
      _diffuseCache.t === field.t &&
      _diffuseCache.cool === cool &&
      _diffuseCache.nodeN === nodeN &&
      _diffuseCache.negN === negN
    ) {
      return _diffuseCache.map;
    }
    var map = diffuseProductScores(field);
    _diffuseCache = {
      t: field.t,
      cool: cool,
      nodeN: nodeN,
      negN: negN,
      map: map
    };
    return map;
  }

  /**
   * True when handle carries high energy relative to the current field.
   * Uses diffuseProductScores (same signal as ranking) so hop-activated tops
   * count; falls back to cheap productEnergy when diffuse is empty/cold.
   */
  function isHighEnergy(handle, field, data) {
    if (!handle) return false;
    field = field || getField();
    data = data || graphData();
    if (!data || !data.products || !data.products[handle]) {
      var direct = (field.nodes && field.nodes['product:' + handle]) || 0;
      return direct >= HIGH_ENERGY_FLOOR;
    }

    var map = null;
    var e = 0;
    var vals = [];
    var i;
    var diffuse = diffuseMapCached(field);
    var dKeys = Object.keys(diffuse);
    if (dKeys.length >= 3 && diffuse[handle] != null && diffuse[handle] > 0) {
      map = diffuse;
      e = diffuse[handle];
      for (i = 0; i < dKeys.length; i++) {
        if (diffuse[dKeys[i]] > 0) vals.push(diffuse[dKeys[i]]);
      }
    } else {
      e = productEnergy(handle, field, data);
      var refs = referenceProductHandles(field, data);
      if (refs.indexOf(handle) === -1) refs.push(handle);
      map = {};
      for (i = 0; i < refs.length; i++) {
        var pe = productEnergy(refs[i], field, data);
        map[refs[i]] = pe;
        if (pe > 0) vals.push(pe);
      }
    }

    if (!vals.length) return false;
    var top = vals[0];
    var sum = 0;
    for (i = 0; i < vals.length; i++) {
      if (vals[i] > top) top = vals[i];
      sum += vals[i];
    }
    if (top < HIGH_ENERGY_FLOOR) return false;
    // Exact / near-top of the energy frontier is always high.
    if (e >= top * 0.98) return true;
    var mean = sum / vals.length;
    if (e >= top * HIGH_ENERGY_REL) return true;
    if (e >= mean * HIGH_ENERGY_MEAN_MULT && e >= top * 0.35) return true;
    var ranked = Object.keys(map).sort(function (a, b) {
      return (map[b] || 0) - (map[a] || 0);
    });
    var idx = ranked.indexOf(handle);
    return idx >= 0 && idx < HIGH_ENERGY_TOP_N && e >= top * 0.25;
  }

  function fieldIsCold(field) {
    field = field || getField();
    var n = Object.keys(field.nodes || {}).length;
    var settles = (field.query && field.query.settles) || 0;
    return n < 2 || (settles < 1 && n < 4);
  }

  /**
   * Energy × engage/skip scale for structural learn / negatives / inject.
   * Engage + high → 1; engage + low → weak; skip + high → strong; skip + low → tiny.
   * Cold start: engage full, skip weak (don't thrash).
   */
  function learningScale(handle, polarity, kind) {
    var field = getField();
    var cold = fieldIsCold(field);
    var high = handle ? isHighEnergy(handle, field) : false;
    if (polarity === 'engage') {
      if (kind === 'listen' || kind === 'atc') return 1;
      if (cold) return 1;
      if (high) return 1;
      if (kind === 'hover' || kind === 'visible') return 0.04;
      return LEARN_LOW_ENGAGE;
    }
    // skip / negative
    if (cold) return LEARN_LOW_SKIP;
    if (high) return LEARN_HIGH_SKIP;
    return LEARN_LOW_SKIP;
  }

  function coolThermostat(amount) {
    if (!(amount > 0)) return getField();
    var field = getField();
    field.cool = Math.min(0.85, (field.cool || 0) + amount);
    applyThermostat(field);
    writeField(field);
    dispatchFieldUpdated(field);
    return field;
  }

  function lexicalScore(handle, stem, data) {
    if (!stem || !data || !data.products[handle]) return 0;
    var p = data.products[handle];
    var blob = (
      (p.t || '') +
      ' ' +
      handle.replace(/-/g, ' ') +
      ' ' +
      (p.a && data.artists[p.a] ? data.artists[p.a].n : '') +
      ' ' +
      (p.g || [])
        .map(function (g) {
          return data.genres[g] ? data.genres[g].n : g;
        })
        .join(' ')
    ).toLowerCase();
    if (blob.indexOf(stem) !== -1) return 100;
    var toks = stem.split(' ');
    var hit = 0;
    var i;
    for (i = 0; i < toks.length; i++) {
      if (toks[i].length >= 2 && blob.indexOf(toks[i]) !== -1) hit++;
    }
    if (!toks.length) return 0;
    return (hit / toks.length) * 70;
  }

  function matchesPrimary(item, primary) {
    if (!primary || !primary.id || !item) return false;
    var parts = String(primary.id).split(':');
    var kind = parts[0];
    var key = parts.slice(1).join(':');
    if (kind === 'genre') {
      return (item.genreKeys || []).indexOf(key) !== -1;
    }
    if (kind === 'artist') {
      return item.artistKey === key;
    }
    if (kind === 'product') {
      return item.handle === key;
    }
    return false;
  }

  /** Strong lexical hit on query stem (e.g. title/artist/genre contains "blues"). */
  function matchesLexicalStem(item, stem, data) {
    if (!item || !stem || stem.length < 2) return false;
    if (item.handle && data) {
      return lexicalScore(item.handle, stem, data) >= 55;
    }
    var blob = (
      (item.title || '') +
      ' ' +
      (item.artist || '') +
      ' ' +
      (item.handle || '').replace(/-/g, ' ') +
      ' ' +
      ((item.genres || []) || []).join(' ')
    ).toLowerCase();
    if (blob.indexOf(stem) !== -1) return true;
    var toks = stem.split(' ');
    var hit = 0;
    var i;
    for (i = 0; i < toks.length; i++) {
      if (toks[i].length >= 2 && blob.indexOf(toks[i]) !== -1) hit++;
    }
    return toks.length > 0 && hit === toks.length;
  }

  /**
   * Title / artist / handle only — does NOT count catalog genre tags.
   * Prevents mis-tagged "blues" products (Piaf, Gardel, Cash) from counting as lexical.
   */
  function titleArtistBlob(item, data) {
    if (!item) return '';
    var artist = item.artist || '';
    if (
      !artist &&
      item.handle &&
      data &&
      data.products &&
      data.products[item.handle]
    ) {
      var p = data.products[item.handle];
      if (p.a && data.artists[p.a]) artist = data.artists[p.a].n || p.a;
    }
    var title = item.title || '';
    if (
      !title &&
      item.handle &&
      data &&
      data.products &&
      data.products[item.handle]
    ) {
      title = data.products[item.handle].t || '';
    }
    return (
      title +
      ' ' +
      (item.handle || '').replace(/-/g, ' ') +
      ' ' +
      artist
    ).toLowerCase();
  }

  function matchesLexicalStrict(item, stem, data) {
    if (!item || !stem || stem.length < 2) return false;
    return titleArtistBlob(item, data).indexOf(stem) !== -1;
  }

  var FOREIGN_TITLE_MARKERS = [
    'tango',
    'calypso',
    'fado',
    'opera',
    'mariachi',
    'flamenco',
    'reggae',
    'salsa',
    'cumbia',
    'bossa',
    'samba',
    'klezmer',
    'celtic',
    'gospel',
    'country',
    'bluegrass',
    'hip hop',
    'hip-hop',
    'metal',
    'punk',
    'edith piaf',
    'carlos gardel',
    'johnny cash',
    'jimmie rodgers',
    'frank sinatra',
    'elvis'
  ];

  function titleForeignConflict(item, stem, data) {
    var blob = titleArtistBlob(item, data);
    if (!blob) return false;
    if (stem && blob.indexOf(stem) !== -1) return false;
    var i;
    for (i = 0; i < FOREIGN_TITLE_MARKERS.length; i++) {
      if (blob.indexOf(FOREIGN_TITLE_MARKERS[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * Intent mass for hard viewport lock.
   * Committed (strict): title/artist/handle stem OR active sub-intent pocket
   * (so desert-blues dwells can surface without every title saying "blues").
   * Lean: primary match (clean) OR strict lexical OR sub-intent.
   */
  function inIntentMass(item, primary, stem, data, opts) {
    opts = opts || {};
    if (!item) return false;
    var strict = !!opts.strict;
    var sub = opts.subIntent || null;
    if (matchesLexicalStrict(item, stem, data)) return true;
    if (sub && matchesSubIntent(item, sub)) {
      // Sub-intent pocket is trusted evidence of "which blues"
      if (!titleForeignConflict(item, stem, data)) return true;
    }
    if (strict) return false;
    // Lean: allow clean primary-only membership
    if (!matchesPrimary(item, primary)) return false;
    if (titleForeignConflict(item, stem, data)) return false;
    if (primary && String(primary.id).indexOf('genre:') === 0) {
      var gKey = String(primary.id).slice(6);
      var keys = item.genreKeys || [];
      if (keys.length === 1 && keys[0] === gKey) return true;
      if (keys[0] === gKey && keys.length <= 2) return true;
      return false;
    }
    return true;
  }

  /**
   * Pull every graph product for the primary genre/artist so committed
   * mode is not starved by a thin RRF candidate list.
   * Also pulls related-genre products (children first, then siblings) capped
   * so hierarchy cannot flood the pool with distant cousins.
   */
  function harvestPrimaryHandles(primary, data) {
    var out = [];
    if (!primary || !primary.id || !data) return out;
    var parts = String(primary.id).split(':');
    var kind = parts[0];
    var key = parts.slice(1).join(':');
    if (kind === 'genre' && data.genres[key] && data.genres[key].p) {
      out = data.genres[key].p.slice();
      var rels = parseGenreRelated(data.genres[key]);
      var seen = {};
      var oi;
      for (oi = 0; oi < out.length; oi++) seen[out[oi]] = true;
      var pass;
      for (pass = 0; pass < 2; pass++) {
        var want = pass === 0 ? 'c' : 's';
        var cap = pass === 0 ? 48 : 24;
        var added = 0;
        var ri;
        for (ri = 0; ri < rels.length && added < cap; ri++) {
          if (rels[ri].kind !== want) continue;
          var rg = data.genres[rels[ri].slug];
          if (!rg || !rg.p) continue;
          var pi;
          for (pi = 0; pi < rg.p.length && added < cap; pi++) {
            if (seen[rg.p[pi]]) continue;
            seen[rg.p[pi]] = true;
            out.push(rg.p[pi]);
            added++;
          }
        }
      }
      return out;
    }
    if (kind === 'artist' && data.artists[key] && data.artists[key].p) {
      return data.artists[key].p.slice();
    }
    if (kind === 'product' && data.products[key]) {
      return [key];
    }
    return out;
  }

  function harvestSubIntentHandles(sub, data) {
    var out = [];
    if (!sub || !data) return out;
    var i;
    if (sub.handles) {
      for (i = 0; i < sub.handles.length; i++) out.push(sub.handles[i]);
    }
    if (sub.genres) {
      for (i = 0; i < sub.genres.length; i++) {
        var g = data.genres[sub.genres[i]];
        if (g && g.p) out = out.concat(g.p.slice(0, 40));
        // One hop of related genres (siblings/parent) for Sahara/Tuareg pocket
        if (g && g.r) {
          var rels = parseGenreRelated(g);
          var ri;
          for (ri = 0; ri < rels.length; ri++) {
            var rg = data.genres[rels[ri].slug];
            if (rg && rg.p) out = out.concat(rg.p.slice(0, 16));
          }
        }
      }
    }
    if (sub.artists) {
      for (i = 0; i < sub.artists.length; i++) {
        var a = data.artists[sub.artists[i]];
        if (a && a.p) out = out.concat(a.p.slice(0, 12));
      }
    }
    return out;
  }

  /** Scan catalog for title/artist/handle stem matches (not genre tags). */
  function harvestLexicalHandles(stem, data, cap) {
    var out = [];
    if (!stem || !data || !data.products) return out;
    var limit = cap || 160;
    var h;
    for (h in data.products) {
      if (!Object.prototype.hasOwnProperty.call(data.products, h)) continue;
      var p = data.products[h];
      var artist = p.a && data.artists[p.a] ? data.artists[p.a].n || '' : '';
      var blob = (
        (p.t || '') +
        ' ' +
        h.replace(/-/g, ' ') +
        ' ' +
        artist
      ).toLowerCase();
      if (blob.indexOf(stem) !== -1) {
        out.push(h);
        if (out.length >= limit) break;
      }
    }
    return out;
  }

  function rrfFuse(rankLists, weights) {
    var scores = {};
    var li;
    for (li = 0; li < rankLists.length; li++) {
      var list = rankLists[li].handles || [];
      var w = rankLists[li].weight != null ? rankLists[li].weight : 1;
      var i;
      for (i = 0; i < list.length; i++) {
        var h = list[i];
        if (!h) continue;
        scores[h] = (scores[h] || 0) + (w * 1) / (RRF_K + i + 1);
      }
    }
    return Object.keys(scores).sort(function (a, b) {
      return scores[b] - scores[a];
    });
  }

  function mmrPick(items, n, scoreFn) {
    var out = [];
    var used = {};
    var artistCount = {};
    var pool = (items || []).slice();
    while (out.length < n && pool.length) {
      var bestIdx = -1;
      var bestAdj = -Infinity;
      var i;
      for (i = 0; i < pool.length; i++) {
        var it = pool[i];
        if (!it || !it.handle || used[it.handle]) continue;
        var base = scoreFn(it);
        var ak = it.artistKey || '';
        var pen = ak && artistCount[ak] ? artistCount[ak] * 0.45 * Math.abs(base) : 0;
        var adj = base - pen;
        if (adj > bestAdj) {
          bestAdj = adj;
          bestIdx = i;
        }
      }
      if (bestIdx < 0) break;
      var pick = pool.splice(bestIdx, 1)[0];
      used[pick.handle] = true;
      out.push(pick);
      if (pick.artistKey) {
        artistCount[pick.artistKey] = (artistCount[pick.artistKey] || 0) + 1;
      }
    }
    return out;
  }

  /**
   * Learn: nudge term weights after positive/negative evidence.
   * Gated by energy × engage/skip when opts.handle is set (learning that matters).
   */
  function learnFromSignal(kind, positive, opts) {
    var handle = null;
    if (typeof opts === 'string') {
      handle = opts;
      opts = { handle: handle };
    } else {
      opts = opts || {};
      handle = opts.handle || null;
    }
    var scale = 1;
    if (handle) {
      scale = learningScale(handle, positive ? 'engage' : 'skip', kind);
    } else if (kind === 'skip' || kind === 'hover' || kind === 'visible') {
      // Ungated skip/dwell without handle — do not thrash term weights.
      return getField();
    }
    if (scale < 0.05) return getField();

    var field = getField();
    var tw = field.termWeights || {};
    var key =
      kind === 'hover' || kind === 'visible'
        ? 'behavioral'
        : kind === 'listen' || kind === 'atc'
          ? 'listen'
          : kind === 'skip'
            ? 'behavioral'
            : kind === 'type'
              ? 'energy'
              : 'energy';
    var delta = positive ? 0.04 : -0.03;
    if (kind === 'skip') delta = -0.05;
    delta = delta * (scale >= 0.95 ? 1 : scale);
    tw[key] = Math.min(1.8, Math.max(0.25, (tw[key] != null ? tw[key] : 1) + delta));
    field.termWeights = tw;
    writeField(field);
    return field;
  }

  /**
   * Main ranker: energy diffusion + RRF + committed takeover.
   * Returns null to fall back to legacy rankByAffinity.
   */
  function rankForYou(seedItems, opts) {
    opts = opts || {};
    var field = getField();
    var nodeCount = Object.keys(field.nodes || {}).length;
    if (!nodeCount) return null;
    // Explore with tiny energy → legacy is fine
    if (field.mode === 'explore' && nodeCount < 2 && (field.query.settles || 0) < 1) {
      return null;
    }
    if (!graphReady()) return null;

    var data = graphData();
    var limit = opts.limit || DISCOVER_LIMIT;
    var tw = field.termWeights || DEFAULT_WEIGHTS;
    var energyMap = diffuseProductScores(field);
    var energyHandles = Object.keys(energyMap).sort(function (a, b) {
      return energyMap[b] - energyMap[a];
    });

    var stem = (field.query && field.query.stem) || '';
    var lexicalPairs = [];
    var hi;
    for (hi = 0; hi < energyHandles.length; hi++) {
      lexicalPairs.push({
        h: energyHandles[hi],
        s: lexicalScore(energyHandles[hi], stem, data)
      });
    }
    // Also score seed handles for lexical
    var seed = seedItems || [];
    for (hi = 0; hi < seed.length; hi++) {
      if (!seed[hi] || !seed[hi].handle) continue;
      if (!energyMap[seed[hi].handle]) {
        var ls = lexicalScore(seed[hi].handle, stem, data);
        if (ls > 0) lexicalPairs.push({ h: seed[hi].handle, s: ls });
      }
    }
    lexicalPairs.sort(function (a, b) {
      return b.s - a.s;
    });
    var lexicalHandles = [];
    for (hi = 0; hi < lexicalPairs.length; hi++) {
      if (lexicalPairs[hi].s > 0) lexicalHandles.push(lexicalPairs[hi].h);
    }

    var behavioralHandles = [];
    var productNodes = topNodes(field, 10, 'product:');
    for (hi = 0; hi < productNodes.length; hi++) {
      var ph = String(productNodes[hi].id).replace(/^product:/, '');
      if (!ph) continue;
      behavioralHandles.push(ph);
      var resolved = window.wmnCatalogGraph.resolveProduct(ph);
      if (resolved && resolved.artists && resolved.artists[0]) {
        var neigh =
          window.wmnCatalogGraph.expand(['artist:' + resolved.artists[0]], 1) || [];
        behavioralHandles = behavioralHandles.concat(neigh.slice(0, 20));
      }
    }

    var popularHandles = [];
    for (hi = 0; hi < seed.length; hi++) {
      if (seed[hi] && seed[hi].handle) popularHandles.push(seed[hi].handle);
    }

    var listenHandles = [];
    try {
      if (window.wmnEngagement && typeof window.wmnEngagement.getStore === 'function') {
        var store = window.wmnEngagement.getStore();
        var evs = (store && store.events) || [];
        var ei;
        for (ei = evs.length - 1; ei >= 0 && listenHandles.length < 24; ei--) {
          var ev = evs[ei];
          if (!ev || !ev.h) continue;
          if (ev.k === 'listen' || ev.k === 'atc' || ev.k === 'search_click') {
            listenHandles.push(ev.h);
          }
        }
      }
    } catch (e0) {
      /* ignore */
    }

    var fused = rrfFuse(
      [
        { handles: energyHandles, weight: tw.energy != null ? tw.energy : 1 },
        { handles: lexicalHandles, weight: tw.lexical != null ? tw.lexical : 1 },
        {
          handles: behavioralHandles,
          weight: tw.behavioral != null ? tw.behavioral : 1
        },
        { handles: popularHandles, weight: tw.popular != null ? tw.popular : 0.35 },
        { handles: listenHandles, weight: tw.listen != null ? tw.listen : 1.25 }
      ],
      tw
    );

    var byHandle = {};
    function ensureItem(h) {
      if (byHandle[h]) return byHandle[h];
      var fromSeed = null;
      var si;
      for (si = 0; si < seed.length; si++) {
        if (seed[si] && seed[si].handle === h) {
          fromSeed = seed[si];
          break;
        }
      }
      var item = itemFromHandle(h);
      if (!item && fromSeed) {
        item = {
          handle: fromSeed.handle,
          title: fromSeed.title,
          artist: fromSeed.artist || '',
          url: fromSeed.url,
          image: fromSeed.image || null,
          genres: fromSeed.genres || [],
          artistKey: '',
          genreKeys: []
        };
        if (graphReady()) {
          var r = window.wmnCatalogGraph.resolveProduct(h);
          if (r) {
            item.artistKey = (r.artists && r.artists[0]) || '';
            item.genreKeys = r.genres || [];
          }
        }
      }
      if (item && fromSeed) {
        item.title = item.title || fromSeed.title;
        item.artist = fromSeed.artist || item.artist;
        item.image = item.image || fromSeed.image || null;
        item.url = item.url || fromSeed.url;
      }
      if (item) byHandle[h] = item;
      return item;
    }

    var candidates = [];
    var seen = {};
    for (hi = 0; hi < fused.length && candidates.length < CANDIDATE_CAP; hi++) {
      var fh = fused[hi];
      if (seen[fh]) continue;
      var it = ensureItem(fh);
      if (!it) continue;
      seen[fh] = true;
      it._energy = energyMap[fh] || 0;
      it._rrf = fused.length - hi;
      candidates.push(it);
    }
    for (hi = 0; hi < seed.length && candidates.length < CANDIDATE_CAP; hi++) {
      if (!seed[hi] || !seed[hi].handle || seen[seed[hi].handle]) continue;
      var sit = ensureItem(seed[hi].handle);
      if (!sit) continue;
      seen[seed[hi].handle] = true;
      sit._energy = energyMap[seed[hi].handle] || 0;
      candidates.push(sit);
    }

    function scoreFn(item) {
      var s = (item._energy || 0) * 10 + (item._rrf || 0);
      var sub = field.subIntent;
      if (sub && matchesSubIntent(item, sub)) {
        s += 55;
        if (sub.kind === 'genre') s += 20;
      }
      return s;
    }

    var primary = primaryIntent(field);
    var out = [];
    var isCommitted = field.mode === 'committed';
    var isLean = field.mode === 'lean';
    var subIntent = field.subIntent || null;
    // First viewport lock: committed ~78% (≈25/32), lean ~50%.
    var hardSlots = isCommitted
      ? Math.round(limit * 0.78)
      : isLean
        ? Math.round(limit * 0.5)
        : 0;

    if (hardSlots > 0 && primary) {
      // Widen mass pool beyond RRF — primary + lexical + sub-intent pocket.
      var harvest = harvestPrimaryHandles(primary, data)
        .concat(harvestLexicalHandles(stem, data, 160))
        .concat(harvestSubIntentHandles(subIntent, data));
      for (hi = 0; hi < harvest.length && candidates.length < CANDIDATE_CAP + 80; hi++) {
        var hh = harvest[hi];
        if (!hh || seen[hh]) continue;
        var hitItem = ensureItem(hh);
        if (!hitItem) continue;
        seen[hh] = true;
        hitItem._energy = energyMap[hh] || productEnergy(hh, field, data);
        hitItem._rrf = hitItem._rrf || 0;
        candidates.push(hitItem);
      }

      var massPool = [];
      var otherPool = [];
      var massOpts = { strict: isCommitted, subIntent: subIntent };
      for (hi = 0; hi < candidates.length; hi++) {
        if (inIntentMass(candidates[hi], primary, stem, data, massOpts)) {
          massPool.push(candidates[hi]);
        } else {
          otherPool.push(candidates[hi]);
        }
      }

      // Prefer sub-intent + lexical items first inside mass.
      massPool.sort(function (a, b) {
        var aSub = subIntent && matchesSubIntent(a, subIntent) ? 2 : 0;
        var bSub = subIntent && matchesSubIntent(b, subIntent) ? 2 : 0;
        var aLex = matchesLexicalStrict(a, stem, data) ? 1 : 0;
        var bLex = matchesLexicalStrict(b, stem, data) ? 1 : 0;
        var aRank = aSub + aLex;
        var bRank = bSub + bLex;
        if (bRank !== aRank) return bRank - aRank;
        return scoreFn(b) - scoreFn(a);
      });

      // HARD LOCK: first viewport may only come from intent mass.
      out = mmrPick(massPool, hardSlots, scoreFn);

      var restSeen = {};
      for (hi = 0; hi < out.length; hi++) restSeen[out[hi].handle] = true;

      var restMass = [];
      for (hi = 0; hi < massPool.length; hi++) {
        if (!restSeen[massPool[hi].handle]) restMass.push(massPool[hi]);
      }

      var tailN = limit - out.length;
      if (tailN > 0) {
        // Prefer remaining mass for the tail before any explore.
        var massTail = mmrPick(restMass, tailN, scoreFn);
        out = out.concat(massTail);
        for (hi = 0; hi < massTail.length; hi++) restSeen[massTail[hi].handle] = true;
        tailN = limit - out.length;
      }

      // Committed: no unrelated explore/adjacent tail — leave short rather than leak.
      if (tailN > 0 && !isCommitted) {
        var exploreN = Math.max(0, Math.round(tailN * (field.thermostat || 0.4)));
        var restOther = [];
        for (hi = 0; hi < otherPool.length; hi++) {
          if (!restSeen[otherPool[hi].handle]) restOther.push(otherPool[hi]);
        }
        out = out.concat(mmrPick(restOther, exploreN, scoreFn));
      }
      // If still short: leave short rather than leak unrelated bestsellers.
    } else {
      out = mmrPick(candidates, limit, scoreFn);
    }

    if (out.length < Math.min(8, limit)) return null;
    return out.slice(0, limit);
  }

  function getMode() {
    return getField().mode;
  }

  /** Cheap field-only score for sitewide rankers when lean/committed owns. */
  function scoreHandle(handle) {
    if (!handle || !graphReady()) return 0;
    var field = getField();
    var data = graphData();
    var energy = productEnergy(handle, field, data);
    var item = itemFromHandle(handle);
    if (field.subIntent && item && matchesSubIntent(item, field.subIntent)) {
      energy += 2.5;
    }
    return energy * 120;
  }

  function reset() {
    var field = emptyField();
    writeField(field);
    dispatchFieldUpdated(field);
    return field;
  }

  window.wmnIntentField = {
    getField: getField,
    getMode: getMode,
    inject: inject,
    injectMany: injectMany,
    injectFromProduct: injectFromProduct,
    addNegative: addNegative,
    onSearchSettle: onSearchSettle,
    refreshSearchInject: refreshSearchInject,
    onHover: onHover,
    onVisible: onVisible,
    onClick: onClick,
    onListen: onListen,
    onSkip: onSkip,
    onBounceClear: onBounceClear,
    learnFromSignal: learnFromSignal,
    isHighEnergy: isHighEnergy,
    productEnergy: function (handle) {
      var field = getField();
      return productEnergy(handle, field, graphData());
    },
    learningScale: learningScale,
    coolThermostat: coolThermostat,
    diffuseProductScores: diffuseProductScores,
    rankForYou: rankForYou,
    noteDwell: noteDwell,
    inferSubIntent: inferSubIntent,
    scoreHandle: scoreHandle,
    primaryIntent: function () {
      return primaryIntent(getField());
    },
    getSubIntent: function () {
      var f = getField();
      return f.subIntent || null;
    },
    ownsRanking: function () {
      var m = getMode();
      return m === 'lean' || m === 'committed';
    },
    reset: reset
  };
})(window, document);
